import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, MailCheck, Info } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Logo } from '@/components/Shell';
import { Button, Field, Input } from '@/components/ui';

/**
 * Staff only.
 *
 * Tenants have no password to forget — they sign in with a code texted to the
 * number their landlord already holds — so this screen is reached from the
 * manager sign-in page and nowhere else.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [expiresIn, setExpiresIn] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{
        sent: boolean; emailConfigured: boolean; expiresInMinutes: number;
      }>('/auth/staff/forgot-password', { email: email.trim() });
      setEmailConfigured(res.emailConfigured);
      setExpiresIn(res.expiresInMinutes);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a reset link');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg mesh">
      <div className="w-full max-w-sm">
        <Link to="/login" className="flex items-center gap-2 text-sm text-muted mb-8 hover:text-ink w-fit">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <div className="flex items-center gap-2.5 mb-8">
          <Logo />
          <span className="font-extrabold tracking-tight text-lg">PROVIA</span>
        </div>

        {sent ? (
          <>
            <div className="flex items-center gap-2.5">
              <MailCheck className="h-5 w-5 text-ok" />
              <h1 className="text-title">Check your email</h1>
            </div>
            {/*
              Says "if" rather than "we have", because the server answers the
              same way whether or not the address is registered. Claiming an
              email was sent when none was would be a lie, and confirming which
              addresses have accounts is exactly what that endpoint refuses to
              do.
            */}
            <p className="text-muted mt-3 text-sm leading-relaxed">
              If <span className="text-ink">{email.trim()}</span> has a manager account, a link
              to choose a new password is on its way. It expires in {expiresIn} minutes and
              works once.
            </p>
            <p className="text-muted mt-3 text-sm leading-relaxed">
              Nothing arrived? Check the spam folder, then try again — the address has to
              match the one your organisation was set up with.
            </p>

            {!emailConfigured && (
              <div className="flex items-start gap-2 text-sm text-warn bg-warn/10
                              border border-warn/25 rounded-xl px-3 py-2.5 mt-5">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Email is not switched on for this deployment yet, so the link was written
                  to the backend&rsquo;s execution log instead of being sent.
                </span>
              </div>
            )}

            <div className="mt-8 space-y-3">
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => { setSent(false); }}
              >
                Use a different address
              </Button>
              <p className="text-sm text-faint text-center">
                <Link to="/login" className="link">Back to sign in</Link>
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-title">Forgotten your password?</h1>
            <p className="text-muted mt-2 text-sm leading-relaxed">
              Enter the email address you sign in with and we will send you a link to choose
              a new password.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="username"
                  required
                  autoFocus
                />
              </Field>

              {error && (
                <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                                border border-danger/25 rounded-xl px-3 py-2.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
                Send reset link
              </Button>
            </form>

            <p className="text-sm text-faint mt-8 text-center leading-relaxed">
              Are you a tenant? You have no password —{' '}
              <Link to="/tenant-login" className="link">sign in with your phone number</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
